import { JSX, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';

import { LatLng } from 'leaflet';
import _ from 'lodash';
import { getCoreRowModel } from '@tanstack/react-table';

// Styles
import { useTheme, useMediaQuery, Tab, Grid, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

// Hooks
import useDeviceDetails from '../../../hooks/useDeviceDetails';

// Utils
import AuthService from '../../../services/auth.service';
import { Storage } from '../../../utils/Storage';
import { makeSightingListColumn } from '../../../utils/columns/makeColumn';
import { canReadDevice, canReadPairing, canReadSightingProcessed, canReadVisit } from '../../../utils/permissions';
import { makeVisitHistoryColumns } from '../../../utils/columns/makeVisitHistoryColumn';
import Pairing from './Pairing';

// Types
import { Visit } from '../../../types/Visit';
import { VisitReader } from '../../../types/VisitReader';
import { ResourceType } from '../../../types/ResourceType';
import { ClosestReaderLog } from '../../../types/ClosestReaderLog';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import { AppPermissions } from '../../../constants/Permissions';
import { CustomImageList } from '../../common/CustomImageList';
import { OverviewCard } from '../../common/OverviewCard/OverviewCard';
import Unauthorized from '../../common/Unauthorized';

// Components
import DeviceStage from './deviceStage';
import DeviceRawData from './deviceRawData';
import { Spinner } from '../../common/Spinner';
import VisitHistory from './VisitHistory';
import { SuperSentinelPanel } from './SuperSentinelPanel';
import VisitReplayMap from './VisitReplayMap';

// Styles
import '../../../styles/muiTabs.scss';
import './deviceDetails.scss';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  marginBottom: 2,
  color: theme.palette.text.secondary,
}));

export default function DeviceDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const bySerial = searchParams.get('bySerial') === 'true';

  const location = useLocation();
  const theme = useTheme();

  useEffect(() => {
    if (id && location.pathname.startsWith('/visits/')) {
      Storage.setLastVisitId(id, bySerial);
    }
  }, [id, location.pathname]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [, setCenterMap] = useState<LatLng>();
  const [, setVisitReader] = useState<VisitReader[]>([]);
  const [, setClosestReaderLogs] = useState<ClosestReaderLog[]>([]);
  const [activeCurentTab, setActiveCurentTab] = useState('current');
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const visitReplaySectionRef = useRef<HTMLDivElement | null>(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  const { deviceInfo, pairingInfo, visitInfo, loading, visitsLogInfo, getNearFacility, updateNearReadersPositions, refetchDeviceInfo } =
    useDeviceDetails();

  const activeVisit = useMemo(() => {
    return (
      visitInfo?.find((v) => v.isActive) ??
      visitInfo?.find((v) => v.status?.toLowerCase() === 'active') ??
      visitInfo?.[0] ??
      visitsLogInfo?.find((v) => v.isActive) ??
      visitsLogInfo?.find((v) => v.status?.toLowerCase() === 'active') ??
      visitsLogInfo?.[0] ??
      null
    );
  }, [visitInfo, visitsLogInfo]);

  // The visit the Current tab's "Current Visit" card lists first. Kept on the
  // same source as that card (visitsLogInfo) so the Device Overview in the Visit
  // History tab reports the same visit, preferring an active one when the card
  // holds more than one row.
  const currentVisitSummary = useMemo(() => {
    return (
      visitsLogInfo?.find((v) => v.isActive) ?? visitsLogInfo?.find((v) => v.status?.toLowerCase() === 'active') ?? visitsLogInfo?.[0] ?? null
    );
  }, [visitsLogInfo]);

  const visitId = activeVisit?.visitId;
  const activeVisitId = selectedVisitId ?? visitId;

  const scrollToVisitReplay = useCallback(() => {
    requestAnimationFrame(() => {
      visitReplaySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleVisitMapFocus = useCallback(
    (id: string) => {
      setSelectedVisitId(id);
      scrollToVisitReplay();
    },
    [scrollToVisitReplay],
  );

  const processDeviceData = useCallback(() => {
    if (!id || !visitInfo || !pairingInfo) return;

    const closestFacilityIds: string[] = [];
    if (visitInfo && visitInfo.length > 0) {
      const readers: VisitReader[] = [];
      const closestLogs: ClosestReaderLog[] = [];

      visitInfo.forEach((dv: Visit) => {
        if (dv.closestFacilityId) {
          closestFacilityIds.push(dv.closestFacilityId);
        }

        Object.values(dv.readers ?? {}).forEach((vReader) => {
          if (dv.closestReader?.imei === vReader.imei) {
            vReader.showArrowFlag = true;
          }
          readers.push(vReader);
        });

        closestLogs.push(...dv.closestReaderLog);
      });

      setVisitReader(readers);
      setClosestReaderLogs(_.orderBy(closestLogs, ['timestamp'], ['desc']));

      if (mountedRef.current) {
        setVisitReader(readers);
        setClosestReaderLogs(_.orderBy(closestLogs, ['timestamp'], ['desc']));
      }
      if (readers.length > 0) {
        updateNearReadersPositions(readers);
      }
    }

    if (pairingInfo?.latestSighting?.location) {
      const { location } = pairingInfo.latestSighting;
      setCenterMap(new LatLng(location.latitude, location.longitude));
      getNearFacility(location, closestFacilityIds);
    }
  }, [id, visitInfo, pairingInfo]);

  useEffect(() => {
    if (id) {
      processDeviceData();
    }
  }, [id, processDeviceData]);

  const handleTabchange = (event: React.SyntheticEvent, newValue: string) => setActiveCurentTab(newValue);

  const columns = useMemo(() => makeSightingListColumn(), []);
  const table = useTable<any>({
    columns,
    serverSide: false,
  });

  const visitLogColumns = useMemo(
    () => makeVisitHistoryColumns(handleVisitMapFocus, selectedVisitId, handleVisitMapFocus),
    [handleVisitMapFocus, selectedVisitId],
  );

  const visitsLogTable = useTable<Visit>({
    columns: visitLogColumns,
    serverSide: false,
  });

  return (
    <>
      {canReadDevice || isExternalUser ? (
        <>
          <TabContext value={activeCurentTab}>
            {loading && <Spinner loading />}
            <div className='app-tabs'>
              <TabList onChange={handleTabchange} aria-label='lab API tabs example'>
                <Tab label='Current' value='current' sx={{ textTransform: 'none' }} />

                {!isExternalUser && canReadPairing && <Tab label='Pairing History' value='pairingHistory' sx={{ textTransform: 'none' }} />}

                {canReadVisit && <Tab label='Visit History' value='visitHistory' sx={{ textTransform: 'none' }} />}

                {!isExternalUser && canReadSightingProcessed && <Tab label='Raw Data' value='rawData' sx={{ textTransform: 'none' }} />}
              </TabList>
            </div>
            <TabPanel
              value='current'
              style={{
                height: 'auto',
                overflowY: 'auto',
                padding: 5,
                display: activeCurentTab !== 'current' ? 'none' : undefined, // ? stays mounted, just hidden
              }}
            >
              <>
                {deviceInfo && pairingInfo && <DeviceStage device={deviceInfo} pairing={pairingInfo} />}
                {!isExternalUser && deviceInfo && id && (
                  <Item style={{ marginTop: 5, padding: '8px 0' }}>
                    <SuperSentinelPanel
                      deviceInfo={deviceInfo}
                      statusDeviceId={id}
                      activateDeviceId={id}
                      deactivateDeviceId={id}
                      onDeactivated={refetchDeviceInfo}
                    />
                  </Item>
                )}
                <Grid item>
                  {!isExternalUser && (
                    <Item style={{ marginTop: 5 }}>
                      <OverviewCard title='Current Sighting' className='device-details-table-flat'>
                        <Table
                          {...table}
                          data={pairingInfo?.latestSighting ? [pairingInfo?.latestSighting] : []}
                          showSearchBar={false}
                          showRightAction={false}
                          showColumnToggler={false}
                          showAction={false}
                          showPagination={false}
                          minRows={1}
                          getCoreRowModel={getCoreRowModel()}
                        />
                      </OverviewCard>
                    </Item>
                  )}
                  {AuthService.hasPermission(AppPermissions.Visit.Read) && deviceInfo && (
                    <>
                      <Grid item>
                        <Item style={{ marginTop: 5 }}>
                          <OverviewCard title='Current Visit' className='device-details-table-flat'>
                            <Table
                              {...visitsLogTable}
                              data={visitsLogInfo ?? []}
                              showSearchBar={false}
                              showRightAction={false}
                              showColumnToggler={false}
                              showAction={false}
                              showPagination={false}
                              minRows={1}
                              getCoreRowModel={getCoreRowModel()}
                            />
                          </OverviewCard>
                        </Item>
                      </Grid>
                    </>
                  )}

                  {activeVisitId && (
                    <div ref={visitReplaySectionRef}>
                      <VisitReplayMap
                        visitId={activeVisitId}
                        deviceLocation={pairingInfo?.latestSighting?.location ?? null}
                        canViewSightings={!isExternalUser}
                      />
                    </div>
                  )}
                </Grid>
              </>
            </TabPanel>
            {!isExternalUser && canReadPairing && (
              <TabPanel value='pairingHistory' style={{ height: '630px', overflowY: 'auto', padding: 5 }}>
                {deviceInfo && (
                  <Grid item>
                    <Item style={{ marginTop: 5 }}>
                      <OverviewCard title='Device Images' className='device-details-pairing-card'>
                        <CustomImageList
                          subjectId={deviceInfo.id}
                          subjectIdentifier={deviceInfo.deviceId}
                          imageCols={isMobile ? 4 : 12}
                          imageHeight={120}
                          resourceType={ResourceType.Device}
                        />
                      </OverviewCard>
                    </Item>
                  </Grid>
                )}

                <Grid item>
                  <Item style={{ marginTop: 5 }}>
                    <OverviewCard title='Current Pairing' className='device-details-pairing-card'>
                      <Pairing deviceId={id} />
                    </OverviewCard>
                  </Item>
                </Grid>
              </TabPanel>
            )}

            {canReadVisit && (
              <TabPanel value='visitHistory' style={{ height: 'auto', overflowY: 'auto', padding: 5 }}>
                <VisitHistory
                  currentVisit={activeVisit}
                  currentVisitSummary={currentVisitSummary}
                  onNavigateToCurrentTab={() => setActiveCurentTab('current')}
                />
              </TabPanel>
            )}

            {!isExternalUser && canReadSightingProcessed && (
              <TabPanel value='rawData' style={{ height: 'auto', overflowY: 'auto', padding: 5 }}>
                {deviceInfo && <DeviceRawData device={deviceInfo} />}
              </TabPanel>
            )}
          </TabContext>
        </>
      ) : (
        <Unauthorized />
      )}
    </>
  );
}
